import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

export default function FaqSection({
  title = "Ofte stillede spørgsmål",
  description,
  badge = "Spørgsmål & Svar",
  items = [],
  id = "faq"
}) {
  // Start with all collapsed by default to keep section compact
  const [openIndices, setOpenIndices] = useState([]);

  const toggleItem = (index) => {
    setOpenIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  if (!items || items.length === 0) return null;

  return (
    <section id={id} className="py-10 md:py-14 bg-[#fbf9f5] border-t border-neutral-200/70">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-xl mx-auto mb-6 md:mb-8 space-y-2">
          {badge && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-100/70 border border-brand-200/80 text-brand-800 text-[11px] font-semibold uppercase tracking-wider">
              <HelpCircle className="w-3 h-3 text-brand-700" />
              <span>{badge}</span>
            </div>
          )}
          <h2 className="font-display text-2xl sm:text-3xl font-light text-neutral-950 leading-snug">
            {title}
          </h2>
          {description && (
            <p className="text-neutral-600 font-light text-sm sm:text-base leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-2.5">
          {items.map((item, index) => {
            const isOpen = openIndices.includes(index);
            const questionId = `faq-q-${index}`;
            const answerId = `faq-a-${index}`;

            return (
              <div
                key={index}
                className={`transition-all duration-200 rounded-xl border bg-white overflow-hidden ${
                  isOpen
                    ? 'border-brand-300 shadow-xs ring-1 ring-brand-200/40'
                    : 'border-neutral-200/75 hover:border-brand-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  id={questionId}
                  className="w-full text-left px-4 py-3.5 sm:px-5 sm:py-4 flex items-center justify-between gap-3 cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
                        isOpen
                          ? 'bg-brand-600 text-white'
                          : 'bg-brand-50 text-brand-700 border border-brand-200/60'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <h3 className="font-display font-medium normal-case tracking-normal text-neutral-900 text-base sm:text-[17px] leading-snug">
                      {item.question}
                    </h3>
                  </div>
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${
                      isOpen
                        ? 'rotate-180 bg-brand-50 text-brand-700'
                        : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </div>
                </button>

                {isOpen && (
                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    className="px-4 pb-4 pt-1.5 sm:px-5 sm:pb-4 text-neutral-600 font-light text-sm sm:text-[15px] leading-relaxed border-t border-neutral-100 mt-0.5"
                  >
                    <p className="sm:pl-9 text-neutral-600">{item.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Small reassurance footer inside FAQ */}
        <div className="mt-6 text-center text-xs sm:text-sm text-neutral-500 font-light">
          Har du et andet spørgsmål, som ikke er besvaret her? Du er altid velkommen til at{' '}
          <a href="tel:40938593" className="text-brand-700 font-medium hover:underline">
            ringe på 40 93 85 93
          </a>{' '}
          eller kontakte mig via formularen nedenfor.
        </div>
      </div>
    </section>
  );
}
